import React, { useState, useMemo, useEffect } from 'react';
import { useSyncState } from '../src/hooks/useSyncState';
import { ProjectInfo, GlobalUser, Responsible, Activity, CNCGroup } from '../src/types';

interface PreventionScreenProps {
    projectInfo: ProjectInfo;
    currentUser: GlobalUser;
    hasPermission: (module: string, action: 'view' | 'edit') => boolean;
    responsibles: Responsible[];
    addActivity: (activity: Omit<Activity, 'id' | 'timestamp' | 'count'>) => void;
    levels?: any[];
    units?: any[];
    setNotifications?: React.Dispatch<React.SetStateAction<any[]>>;
    globalSSOMAEvents?: CNCGroup[];
}

// Interfaces for our Prevention module
interface PreventionActivity {
    id: string;
    title: string;
    type: 'charla' | 'epp' | 'comite' | 'inspeccion';
    date: string; // YYYY-MM-DD
    time: string;
    responsibleId: string;
    status: 'Pendiente' | 'Completada';
    notes: string;
    workersAttended?: string[];
    eppItems?: { name: string; quantity: number; delivered: boolean }[];
    inspeccionItems?: { description: string; status: 'cumple' | 'no_cumple' | 'n_a' }[];
    signature?: string;
    completedAt?: string;
}

interface PreventionFinding {
    id: string;
    title: string;
    type: 'SSO' | 'MA';
    severity: 'leve' | 'medio' | 'alto' | 'positivo';
    description: string;
    reporterName: string;
    responsibleId: string;
    status: 'Borrador' | 'Reportado' | 'En Revision' | 'Cerrado';
    dateRaised: string; // YYYY-MM-DD
    targetDate?: string; // YYYY-MM-DD
    photoUrl?: string;
    locationId?: string;
    locationName?: string;
    eventoNoDeseado?: string;
    observations?: string;
    closureNotes?: string;
    closureDate?: string;
    closurePhotoUrl?: string;
    rejectedByExpert?: boolean;
    rejectedComment?: string;
}

const HelmetIcon = ({ className = "size-6" }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 4C7.58 4 4 7.58 4 12v2h16v-2c0-4.42-3.58-8-8-8z" />
        <path d="M11 4.5h2v8h-2z" opacity="0.15" fill="black" />
        <path d="M2 15c0-.55.45-1 1-1h18c.55 0 1 .45 1 1s-.45 1-1 1H3c-.55 0-1-.45-1-1z" />
    </svg>
);

// Preset photo options to simulate camera captures in construction sites
const PRESET_PHOTOS = [
    { id: 'cable', label: '🔌 Cables eléctricos expuestos', url: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&q=80&w=400' },
    { id: 'baranda', label: '🚧 Falta baranda perimetral losa 4', url: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&q=80&w=400' },
    { id: 'escombros', label: '🗑️ Acumulación de escombros en salidas', url: 'https://images.unsplash.com/photo-1590069261209-f8e9b8642343?auto=format&fit=crop&q=80&w=400' },
    { id: 'andamio', label: '🧗 Escalera sin apoyo antideslizante', url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&q=80&w=400' },
];

export default function PreventionScreen({
    projectInfo,
    currentUser,
    hasPermission,
    responsibles = [],
    addActivity,
    levels = [],
    units = [],
    setNotifications,
    globalSSOMAEvents = []
}: PreventionScreenProps) {
    const collectionName = projectInfo.id ? `project_${projectInfo.id}` : 'data';

    // --- State Hook Syncing ---
    const [activitiesList, setActivitiesList] = useSyncState<PreventionActivity[]>(
        collectionName,
        'preventionActivities',
        []
    );

    const [findingsList, setFindingsList] = useSyncState<PreventionFinding[]>(
        collectionName,
        'preventionFindings',
        []
    );

    // --- Tab States ---
    const [activeTab, setActiveTab] = useState<'actividades' | 'hallazgos'>('actividades');
    const [activeSubTab, setActiveSubTab] = useState<string>('planificar'); // activities: planificar, mis_tareas, monitoreo, plantillas; findings: reportar, ver

    // --- Filter States ---
    const [filterType, setFilterType] = useState<string>('all');
    const [filterResponsible, setFilterResponsible] = useState<string>('all');
    const [filterStatus, setFilterStatus] = useState<string>('all');

    // Responsables ordenados alfabéticamente A-Z
    const sortedResponsibles = useMemo(() => {
        return [...(responsibles || [])].sort((a, b) => 
            (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
        );
    }, [responsibles]);

    // --- Modal States ---
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [showCompleteModal, setShowCompleteModal] = useState<PreventionActivity | null>(null);
    const [showFindingDetailModal, setShowFindingDetailModal] = useState<PreventionFinding | null>(null);

    // --- Form States: Schedule Activity ---
    const [newActTitle, setNewActTitle] = useState('');
    const [newActType, setNewActType] = useState<'charla' | 'epp' | 'comite' | 'inspeccion'>('charla');
    const [newActDate, setNewActDate] = useState(new Date().toISOString().split('T')[0]);
    const [newActTime, setNewActTime] = useState('08:00');
    const [newActResponsible, setNewActResponsible] = useState(currentUser?.id || '');
    const [newActNotes, setNewActNotes] = useState('');

    // --- Form States: Report Finding ---
    const [newFindTitle, setNewFindTitle] = useState('');
    const [newFindType, setNewFindType] = useState<'SSO' | 'MA'>('SSO');
    const [newFindSeverity, setNewFindSeverity] = useState<'leve' | 'medio' | 'alto' | 'positivo'>('medio');
    const [newFindDesc, setNewFindDesc] = useState('');
    const [newFindResp, setNewFindResp] = useState('');
    const [newFindDaysToClose, setNewFindDaysToClose] = useState('3');
    const [newFindPhotoPreset, setNewFindPhotoPreset] = useState('');
    const [newFindCustomPhotoUrl, setNewFindCustomPhotoUrl] = useState('');
    const [newFindLocation, setNewFindLocation] = useState('');
    const [newFindEvent, setNewFindEvent] = useState('');
    const [newFindDateRaised, setNewFindDateRaised] = useState(new Date().toISOString().split('T')[0]);
    const [newFindTargetDate, setNewFindTargetDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() + 3);
        return d.toISOString().split('T')[0];
    });

    // --- Form States: Completion Details ---
    const [attendedWorkers, setAttendedWorkers] = useState<string>('');
    const [eppChecked, setEppChecked] = useState<Record<string, boolean>>({});
    const [inspeccionCheck, setInspeccionCheck] = useState<Record<string, 'cumple' | 'no_cumple' | 'n_a'>>({});
    const [signatureName, setSignatureName] = useState('');

    const [editingBorradorId, setEditingBorradorId] = useState<string | null>(null);
    const [selectedReassignId, setSelectedReassignId] = useState('');
    const [cierreComment, setCierreComment] = useState('');
    const [cierrePhoto, setCierrePhoto] = useState('');
    const [rejectionComment, setRejectionComment] = useState('');
    const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

    const isUserAdmin = useMemo(() => {
        if (!currentUser) return false;
        if (currentUser.globalRole === 'Administrador') return true;
        const projRole = currentUser.assignedProjects?.find(p => p.projectId === projectInfo.id)?.profileName || '';
        if (projRole.toLowerCase().includes('admin') || projRole.toLowerCase().includes('director')) return true;
        const resp = responsibles.find(r => r.email.toLowerCase() === currentUser.email.toLowerCase() || r.id === currentUser.id);
        if (resp?.role?.toLowerCase()?.includes('admin')) return true;
        return false;
    }, [currentUser, projectInfo.id, responsibles]);

    const isUserExperto = useMemo(() => {
        if (isUserAdmin) return true;
        if (!currentUser) return false;
        const projRole = currentUser.assignedProjects?.find(p => p.projectId === projectInfo.id)?.profileName || '';
        const isProjExpert = projRole.toLowerCase().includes('prevencionista') || 
                             projRole.toLowerCase().includes('ssoma') || 
                             projRole.toLowerCase().includes('experto') || 
                             projRole.toLowerCase().includes('seguridad') ||
                             projRole.toLowerCase().includes('hseq');
        if (isProjExpert) return true;
        const resp = responsibles.find(r => r.email.toLowerCase() === currentUser.email.toLowerCase() || r.id === currentUser.id);
        if (resp) {
            const rRole = resp.role.toLowerCase();
            return rRole.includes('prevencionista') || 
                   rRole.includes('ssoma') || 
                   rRole.includes('experto') || 
                   rRole.includes('seguridad') ||
                   rRole.includes('hseq');
        }
        return false;
    }, [currentUser, projectInfo.id, responsibles, isUserAdmin]);

    const isFormComplete = useMemo(() => {
        return !!(
            newFindTitle.trim() &&
            newFindLocation &&
            newFindResp &&
            newFindCustomPhotoUrl &&
            newFindDesc.trim()
        );
    }, [newFindTitle, newFindLocation, newFindResp, newFindCustomPhotoUrl, newFindDesc]);



    // Switch subtabs automatically when parent tab changes
    useEffect(() => {
        if (activeTab === 'actividades') {
            setActiveSubTab('planificar');
        } else {
            setActiveSubTab('ver');
        }
    }, [activeTab]);

    // Helpers
    const getResponsibleName = (id: string) => {
        return responsibles.find(r => r.id === id)?.name || 'Sin Asignar';
    };

    // --- Action Handlers: Programar Actividad ---
    const handleProgramActivity = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newActTitle.trim()) return;

        const newAct: PreventionActivity = {
            id: `act-${Date.now()}`,
            title: newActTitle,
            type: newActType,
            date: newActDate,
            time: newActTime,
            responsibleId: newActResponsible,
            status: 'Pendiente',
            notes: newActNotes
        };

        // Initialize sub-items based on type
        if (newActType === 'epp') {
            newAct.eppItems = [
                { name: 'Lentes de seguridad', quantity: 5, delivered: false },
                { name: 'Guantes de cabritilla', quantity: 5, delivered: false },
                { name: 'Casco de seguridad', quantity: 2, delivered: false }
            ];
        } else if (newActType === 'inspeccion') {
            newAct.inspeccionItems = [
                { description: 'Orden y limpieza general del área de trabajo', status: 'n_a' },
                { description: 'Herramientas manuales en buen estado (con cinta de inspección del mes)', status: 'n_a' },
                { description: 'Extintores operativos y vigentes en el sector', status: 'n_a' },
                { description: 'EPP adecuado al riesgo en uso por todo el personal', status: 'n_a' }
            ];
        }

        setActivitiesList(prev => [newAct, ...prev]);
        addActivity({
            title: `Planificó ${newActType === 'charla' ? 'charla' : newActType === 'epp' ? 'entrega de EPP' : newActType === 'inspeccion' ? 'inspección' : 'comité'}: ${newActTitle}`,
            description: `Se agendó la actividad preventora para el día ${newActDate} a las ${newActTime} hrs.`,
            type: 'prevention',
            user: currentUser?.name || 'Prevencionista'
        });

        // Reset Form
        setNewActTitle('');
        setNewActNotes('');
        setNewActType('charla');
        setShowPlanModal(false);
    };

    // --- Action Handlers: Completar Actividad ---
    const openCompletionModal = (activity: PreventionActivity) => {
        setShowCompleteModal(activity);
        setSignatureName('');
        
        if (activity.type === 'charla') {
            setAttendedWorkers('');
        } else if (activity.type === 'epp') {
            const checks: Record<string, boolean> = {};
            activity.eppItems?.forEach((item, idx) => {
                checks[idx] = item.delivered;
            });
            setEppChecked(checks);
        } else if (activity.type === 'inspeccion') {
            const checks: Record<string, 'cumple' | 'no_cumple' | 'n_a'> = {};
            activity.inspeccionItems?.forEach((item, idx) => {
                checks[idx] = item.status;
            });
            setInspeccionCheck(checks);
        }
    };

    const handleCompleteActivity = (e: React.FormEvent) => {
        e.preventDefault();
        if (!showCompleteModal) return;

        setActivitiesList(prev => prev.map(act => {
            if (act.id === showCompleteModal.id) {
                const updated: PreventionActivity = {
                    ...act,
                    status: 'Completada',
                    completedAt: new Date().toISOString().split('T')[0],
                    signature: signatureName || currentUser.name
                };

                if (act.type === 'charla') {
                    updated.workersAttended = attendedWorkers.split('\n').map(w => w.trim()).filter(w => w !== '');
                } else if (act.type === 'epp') {
                    updated.eppItems = act.eppItems?.map((item, idx) => ({
                        ...item,
                        delivered: !!eppChecked[idx]
                    }));
                } else if (act.type === 'inspeccion') {
                    updated.inspeccionItems = act.inspeccionItems?.map((item, idx) => ({
                        ...item,
                        status: inspeccionCheck[idx] || 'n_a'
                    }));
                }
                return updated;
            }
            return act;
        }));

        addActivity({
            title: `Completó actividad de prevención: ${showCompleteModal.title}`,
            description: `Registro firmado y cerrado electrónicamente.`,
            type: 'prevention',
            user: currentUser?.name || 'Prevencionista'
        });

        setShowCompleteModal(null);
    };

    // --- Action Handlers: Reportar Hallazgo ---
    const handleReportFinding = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!newFindTitle.trim()) {
            alert('Por favor ingrese al menos un título para el hallazgo.');
            return;
        }

        const targetStatus = isFormComplete ? 'Reportado' : 'Borrador';

        // Determine location name
        let locName = 'No especificado';
        let locId = newFindLocation;
        if (newFindLocation.startsWith('level-')) {
            const lvlId = newFindLocation.replace('level-', '');
            const lvl = levels.find(l => l.id === lvlId);
            locName = lvl ? lvl.name : 'Nivel';
        } else if (newFindLocation.startsWith('unit-')) {
            const untId = newFindLocation.replace('unit-', '');
            const unt = units.find(u => u.id === untId);
            const lvl = levels.find(l => l.id === unt?.levelId);
            locName = lvl ? `${lvl.name} - ${unt?.name}` : (unt?.name || 'Unidad');
        }

        const isPositivo = newFindSeverity === 'positivo';

        const updatedFinding: PreventionFinding = {
            id: editingBorradorId || `find-${Date.now()}`,
            title: newFindTitle,
            type: newFindType,
            severity: newFindSeverity,
            description: newFindDesc,
            reporterName: currentUser?.name || 'Prevencionista',
            responsibleId: newFindResp,
            status: isPositivo && isFormComplete ? 'Cerrado' : targetStatus,
            dateRaised: newFindDateRaised,
            targetDate: isPositivo ? undefined : newFindTargetDate,
            photoUrl: newFindCustomPhotoUrl || '',
            locationId: locId,
            locationName: locName,
            eventoNoDeseado: newFindEvent,
            closureNotes: isPositivo && isFormComplete ? 'Registrado como reconocimiento positivo de buenas prácticas' : undefined,
            closureDate: isPositivo && isFormComplete ? newFindDateRaised : undefined
        };

        if (editingBorradorId) {
            setFindingsList(prev => prev.map(f => f.id === editingBorradorId ? updatedFinding : f));
        } else {
            setFindingsList(prev => [updatedFinding, ...prev]);
        }

        addActivity({
            title: isFormComplete 
                ? `Reportó hallazgo de seguridad: ${newFindTitle} (${newFindSeverity.toUpperCase()})`
                : `Guardó borrador de hallazgo: ${newFindTitle}`,
            description: isFormComplete 
                ? `Se levantó un hallazgo de tipo ${newFindType} en obra y se envió al responsable.`
                : `Se guardó un borrador preliminar localmente.`,
            type: 'prevention',
            user: currentUser?.name || 'Prevencionista'
        });

        // Trigger notification to the responsible user if complete & reported
        if (isFormComplete && setNotifications && newFindResp) {
            const newNotif = {
                id: `notif-prevention-${Date.now()}`,
                userId: newFindResp,
                title: `Nuevo hallazgo asignado: ${newFindTitle}`,
                description: `Se te ha asignado el hallazgo (${newFindSeverity.toUpperCase()}) en: ${locName}. Plazo de cierre: ${newFindSeverity === 'positivo' ? 'No requiere' : newFindTargetDate}`,
                date: new Date().toISOString(),
                isRead: false,
                type: 'prevention' as any,
                link: '/prevention'
            };
            setNotifications(prev => [newNotif, ...(prev || [])]);
        }

        // Reset Form
        setNewFindTitle('');
        setNewFindDesc('');
        setNewFindResp('');
        setNewFindPhotoPreset('');
        setNewFindCustomPhotoUrl('');
        setNewFindLocation('');
        setNewFindEvent('');
        setEditingBorradorId(null);
        setNewFindDateRaised(new Date().toISOString().split('T')[0]);
        setNewFindTargetDate(() => {
            const d = new Date();
            d.setDate(d.getDate() + 3);
            return d.toISOString().split('T')[0];
        });
        
        // Redirect to Ver Hallazgos
        setActiveSubTab('ver');
    };

    const handleEditBorrador = (finding: PreventionFinding) => {
        setEditingBorradorId(finding.id);
        setNewFindTitle(finding.title);
        setNewFindType(finding.type);
        setNewFindSeverity(finding.severity);
        setNewFindDesc(finding.description);
        setNewFindResp(finding.responsibleId);
        setNewFindCustomPhotoUrl(finding.photoUrl || '');
        setNewFindLocation(finding.locationId || '');
        setNewFindEvent(finding.eventoNoDeseado || '');
        setNewFindDateRaised(finding.dateRaised);
        setNewFindTargetDate(finding.targetDate || '');
        setActiveSubTab('reportar');
    };

    const handleReassign = () => {
        if (!selectedReassignId) return;
        if (!showFindingDetailModal) return;

        setFindingsList(prev => prev.map(f => {
            if (f.id === showFindingDetailModal.id) {
                return { ...f, responsibleId: selectedReassignId };
            }
            return f;
        }));

        addActivity({
            title: `Reasignó responsable de hallazgo: ${showFindingDetailModal.title}`,
            description: `Nuevo encargado: ${getResponsibleName(selectedReassignId)}`,
            type: 'prevention',
            user: currentUser?.name || 'Prevencionista'
        });

        if (setNotifications) {
            const newNotif = {
                id: `notif-prevention-reassign-${Date.now()}`,
                userId: selectedReassignId,
                title: `Hallazgo asignado a tu cargo: ${showFindingDetailModal.title}`,
                description: `Se te ha asignado como responsable de la desviación.`,
                date: new Date().toISOString(),
                isRead: false,
                type: 'prevention' as any,
                link: '/prevention'
            };
            setNotifications(prev => [newNotif, ...(prev || [])]);
        }

        setShowFindingDetailModal(null);
        setSelectedReassignId('');
    };

    const handleCierreTecnico = (e: React.FormEvent) => {
        e.preventDefault();
        if (!showFindingDetailModal) return;
        if (!cierreComment.trim() || !cierrePhoto) {
            alert('Por favor complete la acción correctiva y adjunte la fotografía de la reparación.');
            return;
        }

        setFindingsList(prev => prev.map(f => {
            if (f.id === showFindingDetailModal.id) {
                return {
                    ...f,
                    status: 'En Revision',
                    closureNotes: cierreComment,
                    closurePhotoUrl: cierrePhoto,
                    closureDate: new Date().toISOString().split('T')[0]
                };
            }
            return f;
        }));

        addActivity({
            title: `Envió hallazgo a revisión: ${showFindingDetailModal.title}`,
            description: `Acciones tomadas: ${cierreComment}`,
            type: 'prevention',
            user: currentUser?.name || 'Prevencionista'
        });

        if (setNotifications) {
            const expertUsers = responsibles.filter(r => 
                r.role.toLowerCase().includes('prevencionista') || 
                r.role.toLowerCase().includes('ssoma') || 
                r.role.toLowerCase().includes('admin')
            );
            
            expertUsers.forEach(exp => {
                const newNotif = {
                    id: `notif-prevention-review-${Date.now()}-${exp.id}`,
                    userId: exp.id,
                    title: `Revisión pendiente de hallazgo: ${showFindingDetailModal.title}`,
                    description: `El responsable reportó la resolución. Aprobación requerida.`,
                    date: new Date().toISOString(),
                    isRead: false,
                    type: 'prevention' as any,
                    link: '/prevention'
                };
                setNotifications(prev => [newNotif, ...(prev || [])]);
            });
        }

        setShowFindingDetailModal(null);
        setCierreComment('');
        setCierrePhoto('');
    };

    const handleRechazar = () => {
        if (!showFindingDetailModal) return;
        if (!rejectionComment.trim()) {
            alert('Por favor ingrese el motivo del rechazo.');
            return;
        }

        setFindingsList(prev => prev.map(f => {
            if (f.id === showFindingDetailModal.id) {
                return {
                    ...f,
                    status: 'Reportado',
                    rejectedByExpert: true,
                    rejectedComment: rejectionComment
                };
            }
            return f;
        }));

        addActivity({
            title: `Rechazó resolución de hallazgo: ${showFindingDetailModal.title}`,
            description: `Motivo del rechazo: ${rejectionComment}`,
            type: 'prevention',
            user: currentUser?.name || 'Prevencionista'
        });

        if (setNotifications && showFindingDetailModal.responsibleId) {
            const newNotif = {
                id: `notif-prevention-rejected-${Date.now()}`,
                userId: showFindingDetailModal.responsibleId,
                title: `🚨 Resolución rechazada: ${showFindingDetailModal.title}`,
                description: `El Experto SSOMA rechazó la reparación. Motivo: ${rejectionComment}`,
                date: new Date().toISOString(),
                isRead: false,
                type: 'prevention' as any,
                link: '/prevention'
            };
            setNotifications(prev => [newNotif, ...(prev || [])]);
        }

        setShowFindingDetailModal(null);
        setRejectionComment('');
    };

    const handleAprobar = () => {
        if (!showFindingDetailModal) return;

        setFindingsList(prev => prev.map(f => {
            if (f.id === showFindingDetailModal.id) {
                return {
                    ...f,
                    status: 'Cerrado',
                    closureDate: new Date().toISOString().split('T')[0],
                    rejectedByExpert: false
                };
            }
            return f;
        }));

        addActivity({
            title: `Aprobó cierre técnico de hallazgo: ${showFindingDetailModal.title}`,
            description: `Hallazgo cerrado definitivamente.`,
            type: 'prevention',
            user: currentUser?.name || 'Prevencionista'
        });

        if (setNotifications && showFindingDetailModal.responsibleId) {
            const newNotif = {
                id: `notif-prevention-approved-${Date.now()}`,
                userId: showFindingDetailModal.responsibleId,
                title: `✅ Cierre aprobado: ${showFindingDetailModal.title}`,
                description: `El Experto SSOMA aprobó la reparación y cerró el hallazgo.`,
                date: new Date().toISOString(),
                isRead: false,
                type: 'prevention' as any,
                link: '/prevention'
            };
            setNotifications(prev => [newNotif, ...(prev || [])]);
        }

        setShowFindingDetailModal(null);
    };

    // --- Action Handlers: Cargar Plantillas (Kits) ---
    const handleLoadTemplate = (kitName: string, items: { title: string; type: 'charla' | 'epp' | 'inspeccion'; notes: string }[]) => {
        const newActs: PreventionActivity[] = items.map((item, idx) => {
            const dateObj = new Date();
            dateObj.setDate(dateObj.getDate() + idx); // space items by 1 day
            const act: PreventionActivity = {
                id: `act-kit-${Date.now()}-${idx}`,
                title: `${kitName}: ${item.title}`,
                type: item.type,
                date: dateObj.toISOString().split('T')[0],
                time: '08:00',
                responsibleId: currentUser?.id || 'admin',
                status: 'Pendiente',
                notes: item.notes
            };

            if (item.type === 'epp') {
                act.eppItems = [
                    { name: 'Guantes de cuero/cabritilla reforzados', quantity: 5, delivered: false },
                    { name: 'Arnés anticaídas con cabo de vida', quantity: 3, delivered: false },
                    { name: 'Lentes de protección UV herméticos', quantity: 5, delivered: false }
                ];
            } else if (item.type === 'inspeccion') {
                act.inspeccionItems = [
                    { description: 'Inspección de puntos de anclaje fijos/líneas estructurales', status: 'n_a' },
                    { description: 'Certificado de andamios y tarjetas de color actualizadas', status: 'n_a' },
                    { description: 'Inspección visual de costuras en arneses', status: 'n_a' },
                    { description: 'Señalización del área de caída de materiales instalada', status: 'n_a' }
                ];
            }
            return act;
        });

        setActivitiesList(prev => [...newActs, ...prev]);
        addActivity({
            title: `Cargó plantilla preventiva: ${kitName}`,
            description: `Se programaron actividades preventivas asociadas al kit.`,
            type: 'prevention',
            user: currentUser?.name || 'Prevencionista'
        });

        alert(`Se han programado exitosamente ${items.length} actividades del "${kitName}" en el calendario.`);
        setActiveSubTab('planificar');
    };

    // --- Computed Stats (Monitoreo General) ---
    const stats = useMemo(() => {
        const total = activitiesList.length;
        const completed = activitiesList.filter(a => a.status === 'Completada').length;
        const pending = total - completed;
        const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

        const breakdown = {
            charla: { total: 0, completed: 0 },
            epp: { total: 0, completed: 0 },
            inspeccion: { total: 0, completed: 0 },
            comite: { total: 0, completed: 0 }
        };

        activitiesList.forEach(a => {
            if (breakdown[a.type]) {
                breakdown[a.type].total++;
                if (a.status === 'Completada') {
                    breakdown[a.type].completed++;
                }
            }
        });

        return { total, completed, pending, pct, breakdown };
    }, [activitiesList]);

    // Filtered lists
    const filteredActivities = useMemo(() => {
        return activitiesList.filter(act => {
            const matchesType = filterType === 'all' || act.type === filterType;
            const matchesResp = filterResponsible === 'all' || act.responsibleId === filterResponsible;
            const matchesStatus = filterStatus === 'all' || act.status === filterStatus;
            return matchesType && matchesResp && matchesStatus;
        });
    }, [activitiesList, filterType, filterResponsible, filterStatus]);

    const myActivities = useMemo(() => {
        return activitiesList.filter(act => act.responsibleId === currentUser?.id);
    }, [activitiesList, currentUser]);

    // Kits Templates Data
    const templates = [
        {
            title: 'Kit de Excavaciones y Zanjas',
            desc: 'Protocolo preventivo para faenas de excavación profunda, taludes y movimientos de tierra.',
            color: 'from-amber-500 to-orange-600',
            activities: [
                { title: 'Charla: Riesgos de derrumbe y entibación de taludes', type: 'charla' as const, notes: 'Imprescindible para excavaciones superiores a 1.5 metros.' },
                { title: 'Inspección: Estado de entibaciones, taludes y accesos a zanjas', type: 'inspeccion' as const, notes: 'Verificar cuñas de seguridad y pasarelas con barandas.' },
                { title: 'Entrega: Protectores auditivos y mascarillas para polvo sílice', type: 'epp' as const, notes: 'Para operarios expuestos a polvos de chancado y taladros.' }
            ]
        },
        {
            title: 'Kit de Trabajo en Altura',
            desc: 'Herramientas clave para resguardar a operarios en andamios, losas o techumbres.',
            color: 'from-blue-500 to-indigo-600',
            activities: [
                { title: 'Charla: Trauma por suspensión y rescate en altura', type: 'charla' as const, notes: 'Explicar qué hacer tras una caída amortiguada por arnés.' },
                { title: 'Inspección: Revisión diaria de arneses y líneas de vida', type: 'inspeccion' as const, notes: 'Chequeo de costuras, anillos D y amortiguadores de impacto.' },
                { title: 'Entrega: Arneses de 4 puntas y líneas de vida retráctiles', type: 'epp' as const, notes: 'Reposición física registrada.' }
            ]
        },
        {
            title: 'Kit de Trabajos en Caliente',
            desc: 'Estándar preventivo para soldadores, oxicortadores y uso de esmeril angular.',
            color: 'from-red-500 to-rose-600',
            activities: [
                { title: 'Charla: Prevención de amagos de incendio y uso de extintores', type: 'charla' as const, notes: 'Recordar despejar área de materiales combustibles.' },
                { title: 'Inspección: Equipos de soldadura y pantallas protectoras', type: 'inspeccion' as const, notes: 'Control de cables a tierra y mangueras con válvula antirretorno.' },
                { title: 'Entrega: Chaquetas de cuero, polainas y caretas para soldar', type: 'epp' as const, notes: 'Entrega de kit completo de protección térmica.' }
            ]
        }
    ];

    return (
        <div className="flex-1 flex flex-col overflow-hidden bg-background-light dark:bg-background-dark font-display">
            {/* Top Header */}
            <div className="flex-shrink-0 bg-white dark:bg-[#1a2634] border-b border-[#e7ecf3] dark:border-[#2a3644] px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="text-primary p-2 bg-primary/10 rounded-xl flex-shrink-0 flex items-center justify-center">
                        <HelmetIcon className="size-[36px]" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 dark:text-white">Prevención (SSOMA)</h2>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Seguridad, Salud Ocupacional y Medio Ambiente • {projectInfo.name}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Main Tabs Selector */}
                    <div className="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl flex">
                        <button
                            onClick={() => setActiveTab('actividades')}
                            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${activeTab === 'actividades' ? 'bg-white dark:bg-slate-700 text-primary shadow' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}
                        >
                            <span className="material-symbols-outlined text-[16px]">event_note</span>
                            Gestión de Actividades
                        </button>
                        <button
                            onClick={() => setActiveTab('hallazgos')}
                            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${activeTab === 'hallazgos' ? 'bg-white dark:bg-slate-700 text-primary shadow' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}
                        >
                            <span className="material-symbols-outlined text-[16px]">report_problem</span>
                            Control de Hallazgos
                        </button>
                    </div>
                </div>
            </div>

            {/* Sub navigation and Main Content Panel */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Subtabs Menu */}
                <div className="flex-shrink-0 bg-slate-50 dark:bg-[#151f2b] px-6 py-2.5 border-b border-[#e7ecf3] dark:border-[#2a3644] flex items-center justify-between">
                    <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
                        {activeTab === 'actividades' ? (
                            <>
                                {[
                                    { id: 'planificar', label: 'Planificar Actividades', icon: 'calendar_month' },
                                    { id: 'mis_tareas', label: `Mis Actividades (${myActivities.filter(a => a.status === 'Pendiente').length})`, icon: 'assignment_ind' },
                                    { id: 'monitoreo', label: 'Monitoreo General', icon: 'bar_chart' },
                                    { id: 'plantillas', label: 'Plantillas de Actividades', icon: 'library_books' }
                                ].map(sub => (
                                    <button
                                        key={sub.id}
                                        onClick={() => setActiveSubTab(sub.id)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${activeSubTab === sub.id ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-200/50 dark:text-slate-400 dark:hover:bg-slate-800/50'}`}
                                    >
                                        <span className="material-symbols-outlined text-[16px]">{sub.icon}</span>
                                        {sub.label}
                                    </button>
                                ))}
                            </>
                        ) : (
                            <>
                                {[
                                    { id: 'ver', label: 'Ver Hallazgos', icon: 'view_kanban' },
                                    { id: 'reportar', label: 'Reportar Hallazgo', icon: 'add_a_photo' }
                                ].map(sub => (
                                    <button
                                        key={sub.id}
                                        onClick={() => setActiveSubTab(sub.id)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${activeSubTab === sub.id ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-200/50 dark:text-slate-400 dark:hover:bg-slate-800/50'}`}
                                    >
                                        <span className="material-symbols-outlined text-[16px]">{sub.icon}</span>
                                        {sub.label}
                                    </button>
                                ))}
                            </>
                        )}
                    </div>

                    {/* Quick Add Action button for Planificar Actividades */}
                    {activeTab === 'actividades' && activeSubTab === 'planificar' && (
                        <button
                            onClick={() => setShowPlanModal(true)}
                            className="bg-primary hover:bg-primary-dark text-white text-xs font-bold py-1.5 px-3 rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
                        >
                            <span className="material-symbols-outlined text-[16px]">add_circle</span>
                            Programar Actividad
                        </button>
                    )}
                </div>

                {/* Sub-view Content area */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">

                    {/* Sub-view: PLANIFICAR ACTIVIDADES (CALENDARIO DE TRABAJO EN TARJETAS) */}
                    {activeTab === 'actividades' && activeSubTab === 'planificar' && (
                        <div className="space-y-6 animate-fade-in">
                            {/* Filter panel */}
                            <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-wrap gap-4 items-center justify-between">
                                <div className="flex flex-wrap gap-4 items-center">
                                    <div className="flex flex-col gap-1">
                                        <label className="text-[10px] uppercase font-bold text-slate-400">Tipo de Actividad</label>
                                        <select
                                            value={filterType}
                                            onChange={e => setFilterType(e.target.value)}
                                            className="text-xs bg-slate-50 border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg p-2 focus:ring-1 focus:ring-primary outline-none"
                                        >
                                            <option value="all">Todas las actividades</option>
                                            <option value="charla">Charla de 5 Minutos</option>
                                            <option value="epp">Entrega de EPP</option>
                                            <option value="inspeccion">Inspección Programada</option>
                                            <option value="comite">Comité Paritario</option>
                                        </select>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-[10px] uppercase font-bold text-slate-400">Responsable</label>
                                        <select
                                            value={filterResponsible}
                                            onChange={e => setFilterResponsible(e.target.value)}
                                            className="text-xs bg-slate-50 border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg p-2 focus:ring-1 focus:ring-primary outline-none"
                                        >
                                            <option value="all">Todos los responsables</option>
                                            {sortedResponsibles.map(r => (
                                                <option key={r.id} value={r.id}>{r.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-[10px] uppercase font-bold text-slate-400">Estado de Ejecución</label>
                                        <select
                                            value={filterStatus}
                                            onChange={e => setFilterStatus(e.target.value)}
                                            className="text-xs bg-slate-50 border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg p-2 focus:ring-1 focus:ring-primary outline-none"
                                        >
                                            <option value="all">Todos los estados</option>
                                            <option value="Pendiente">Pendiente</option>
                                            <option value="Completada">Completada</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="text-xs text-slate-400 font-medium">
                                    Mostrando {filteredActivities.length} actividades planificadas
                                </div>
                            </div>

                            {/* Activities Schedule Grid (Cards by days) */}
                            {filteredActivities.length === 0 ? (
                                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-12 text-center text-slate-400">
                                    <span className="material-symbols-outlined text-[48px] text-slate-300 dark:text-slate-600 mb-3">event_busy</span>
                                    <p className="text-sm font-semibold">No se encontraron actividades planificadas para los filtros seleccionados.</p>
                                    <p className="text-xs text-slate-400 mt-1">Haz clic en "Programar Actividad" o dirígete a "Plantillas" para registrar nuevos eventos.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {filteredActivities.map(act => (
                                        <div
                                            key={act.id}
                                            className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col justify-between hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                                        >
                                            <div className="p-5 space-y-4">
                                                {/* Header of activity card */}
                                                <div className="flex items-start justify-between">
                                                    <span className={`text-[10px] font-extrabold uppercase px-2 py-1 rounded-full ${
                                                        act.type === 'charla' ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400' :
                                                        act.type === 'epp' ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400' :
                                                        act.type === 'inspeccion' ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400' :
                                                        'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
                                                    }`}>
                                                        {act.type === 'charla' ? '💬 Charla 5 Min' :
                                                         act.type === 'epp' ? '🎒 Entrega EPP' :
                                                         act.type === 'inspeccion' ? '🔍 Inspección' :
                                                         '👥 Comité Paritario'}
                                                    </span>
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-lg ${
                                                        act.status === 'Completada' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                                    }`}>
                                                        {act.status}
                                                    </span>
                                                </div>

                                                {/* Title & Notes */}
                                                <div>
                                                    <h4 className="text-sm font-extrabold text-slate-800 dark:text-white line-clamp-2 leading-snug">{act.title}</h4>
                                                    {act.notes && (
                                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 italic line-clamp-3">"{act.notes}"</p>
                                                    )}
                                                </div>

                                                {/* Meta Info */}
                                                <div className="pt-2 border-t border-slate-100 dark:border-slate-700 flex flex-col gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="material-symbols-outlined text-[16px]">calendar_today</span>
                                                        <span>{act.date} • {act.time} Hrs</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="material-symbols-outlined text-[16px]">person</span>
                                                        <span>{getResponsibleName(act.responsibleId)}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Action footer */}
                                            <div className="px-5 py-3.5 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-700 flex items-center justify-end">
                                                {act.status === 'Pendiente' ? (
                                                    <button
                                                        onClick={() => openCompletionModal(act)}
                                                        className="w-full bg-primary hover:bg-primary-dark text-white text-xs font-bold py-2 px-3 rounded-lg flex items-center justify-center gap-1 transition-colors"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px]">assignment_turned_in</span>
                                                        Completar y Registrar
                                                    </button>
                                                ) : (
                                                    <div className="w-full text-xs text-center text-green-600 dark:text-green-400 font-bold flex items-center justify-center gap-1 py-1">
                                                        <span className="material-symbols-outlined text-[18px]">verified</span>
                                                        Registrado por: {act.signature}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Sub-view: MIS ACTIVIDADES PENDIENTES */}
                    {activeTab === 'actividades' && activeSubTab === 'mis_tareas' && (
                        <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
                            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 space-y-4">
                                <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-primary">assignment_ind</span>
                                    Mis Tareas Preventivas Pendientes
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    Estas actividades están agendadas bajo tu responsabilidad. Completa el registro electrónico al finalizar cada una.
                                </p>
                            </div>

                            {myActivities.filter(a => a.status === 'Pendiente').length === 0 ? (
                                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-12 text-center text-slate-400">
                                    <span className="material-symbols-outlined text-[48px] text-green-500 mb-3">task_alt</span>
                                    <p className="text-sm font-semibold text-slate-700 dark:text-white">¡Al día! No tienes actividades pendientes asignadas.</p>
                                    <p className="text-xs text-slate-400 mt-1">Buen trabajo manteniendo la obra segura hoy.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {myActivities.filter(a => a.status === 'Pendiente').map(act => (
                                        <div
                                            key={act.id}
                                            className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row md:items-center justify-between gap-4"
                                        >
                                            <div className="space-y-2 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-[9px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                                                        act.type === 'charla' ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20' :
                                                        act.type === 'epp' ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20' :
                                                        act.type === 'inspeccion' ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/20' :
                                                        'bg-purple-50 text-purple-600 dark:bg-purple-900/20'
                                                    }`}>
                                                        {act.type}
                                                    </span>
                                                    <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold">{act.date} • {act.time} Hrs</span>
                                                </div>
                                                <h4 className="text-sm font-bold text-slate-800 dark:text-white">{act.title}</h4>
                                                {act.notes && <p className="text-xs text-slate-400 dark:text-slate-400 italic">"{act.notes}"</p>}
                                            </div>
                                            <div className="flex-shrink-0">
                                                <button
                                                    onClick={() => openCompletionModal(act)}
                                                    className="w-full bg-primary hover:bg-primary-dark text-white text-xs font-bold py-2 px-4 rounded-xl flex items-center justify-center gap-1.5 shadow transition-colors"
                                                >
                                                    <span className="material-symbols-outlined text-[16px]">assignment_turned_in</span>
                                                    Completar Registro
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Sub-view: MONITOREO GENERAL (KPI CARDS & GANTT STYLE PROGRESS BARS) */}
                    {activeTab === 'actividades' && activeSubTab === 'monitoreo' && (
                        <div className="space-y-8 animate-fade-in">
                            {/* KPI Metrics */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-bold text-slate-400 uppercase">Actividades Totales</p>
                                        <p className="text-3xl font-black text-slate-800 dark:text-white mt-1">{stats.total}</p>
                                    </div>
                                    <span className="material-symbols-outlined text-[36px] text-blue-500 bg-blue-50 dark:bg-blue-900/20 p-2.5 rounded-xl">calendar_today</span>
                                </div>
                                <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-bold text-slate-400 uppercase">Completadas</p>
                                        <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400 mt-1">{stats.completed}</p>
                                    </div>
                                    <span className="material-symbols-outlined text-[36px] text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 p-2.5 rounded-xl">check_circle</span>
                                </div>
                                <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-bold text-slate-400 uppercase">Pendientes</p>
                                        <p className="text-3xl font-black text-orange-600 dark:text-orange-400 mt-1">{stats.pending}</p>
                                    </div>
                                    <span className="material-symbols-outlined text-[36px] text-orange-500 bg-orange-50 dark:bg-orange-900/20 p-2.5 rounded-xl">pending_actions</span>
                                </div>
                                <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-bold text-slate-400 uppercase">% de Cumplimiento</p>
                                        <p className="text-3xl font-black text-primary mt-1">{stats.pct}%</p>
                                    </div>
                                    <span className="material-symbols-outlined text-[36px] text-primary bg-primary/10 p-2.5 rounded-xl">donut_large</span>
                                </div>
                            </div>

                            {/* Visual Progress Bars (Similar to Gantt style) */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm space-y-6">
                                    <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                        <span className="material-symbols-outlined text-primary">analytics</span>
                                        Avance Preventivo por Categoría
                                    </h3>
                                    
                                    <div className="space-y-4">
                                        {[
                                            { key: 'charla', label: 'Charlas de 5 Minutos', color: 'bg-blue-500' },
                                            { key: 'epp', label: 'Entrega de EPP', color: 'bg-emerald-500' },
                                            { key: 'inspeccion', label: 'Inspecciones Programadas', color: 'bg-amber-500' },
                                            { key: 'comite', label: 'Comité Paritario', color: 'bg-purple-500' }
                                        ].map(item => {
                                            const cat = stats.breakdown[item.key as 'charla' | 'epp' | 'inspeccion' | 'comite'] || { total: 0, completed: 0 };
                                            const pct = cat.total > 0 ? Math.round((cat.completed / cat.total) * 100) : 0;
                                            return (
                                                <div key={item.key} className="space-y-1.5">
                                                    <div className="flex items-center justify-between text-xs">
                                                        <span className="font-bold text-slate-700 dark:text-slate-200">{item.label}</span>
                                                        <span className="text-slate-500 dark:text-slate-400">{cat.completed} de {cat.total} ({pct}%)</span>
                                                    </div>
                                                    <div className="w-full h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full ${item.color} rounded-full transition-all duration-500`}
                                                            style={{ width: `${pct}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col justify-between">
                                    <div className="space-y-3">
                                        <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                            <span className="material-symbols-outlined text-primary">health_and_safety</span>
                                            Indicador de Obra Segura
                                        </h3>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            La tasa global de cumplimiento preventivo ideal en Constructora LD debe superar el **85%** para mantener estándares óptimos de faena.
                                        </p>
                                    </div>

                                    {/* Giant progress radial indicator */}
                                    <div className="flex flex-col items-center justify-center py-6">
                                        <div className="relative size-32 flex items-center justify-center">
                                            {/* Svg circle */}
                                            <svg className="absolute inset-0 size-full transform -rotate-90">
                                                <circle cx="64" cy="64" r="54" className="stroke-slate-100 dark:stroke-slate-700 fill-none" strokeWidth="10" />
                                                <circle cx="64" cy="64" r="54" className="stroke-primary fill-none transition-all duration-700" strokeWidth="10" strokeDasharray={339.2} strokeDashoffset={339.2 - (339.2 * stats.pct) / 100} />
                                            </svg>
                                            <div className="text-center z-10">
                                                <span className="text-2xl font-black text-slate-800 dark:text-white">{stats.pct}%</span>
                                                <p className="text-[9px] uppercase font-bold text-slate-400">Logrado</p>
                                            </div>
                                        </div>
                                        <p className={`text-xs font-extrabold mt-4 px-3 py-1 rounded-full ${
                                            stats.pct >= 85 ? 'bg-green-50 text-green-600 dark:bg-green-900/20' : 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/20'
                                        }`}>
                                            {stats.pct >= 85 ? 'Rendimiento Óptimo (Zona Segura)' : 'Rendimiento Crítico (Aumentar Charlas/Inspección)'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Sub-view: PLANTILLAS DE ACTIVIDADES (KITS PRECONSTRUIDOS) */}
                    {activeTab === 'actividades' && activeSubTab === 'plantillas' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 space-y-3">
                                <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-primary">library_books</span>
                                    Biblioteca de Estándares por Faena
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    Carga masivamente paquetes de actividades preventivas prediseñadas al calendario para agilizar el plan mensual según el tipo de obra.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {templates.map((kit, idx) => (
                                    <div
                                        key={idx}
                                        className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow"
                                    >
                                        <div>
                                            {/* Kit Header Banner */}
                                            <div className={`p-5 bg-gradient-to-r ${kit.color} text-white`}>
                                                <h4 className="font-extrabold text-base">{kit.title}</h4>
                                                <p className="text-[11px] opacity-90 mt-1">{kit.desc}</p>
                                            </div>

                                            {/* List of included sub-activities */}
                                            <div className="p-5 space-y-4">
                                                <p className="text-[10px] uppercase font-bold text-slate-400">Actividades Incluidas:</p>
                                                <div className="space-y-3">
                                                    {kit.activities.map((a, aIdx) => (
                                                        <div key={aIdx} className="flex gap-2 items-start text-xs">
                                                            <span className="material-symbols-outlined text-[16px] text-primary flex-shrink-0 mt-0.5">
                                                                {a.type === 'charla' ? 'chat' : a.type === 'inspeccion' ? 'search' : 'backpack'}
                                                            </span>
                                                            <div>
                                                                <p className="font-bold text-slate-700 dark:text-slate-200">{a.title}</p>
                                                                <p className="text-[10px] text-slate-400 mt-0.5">{a.notes}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-5 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                                            <button
                                                onClick={() => handleLoadTemplate(kit.title, kit.activities)}
                                                className="w-full bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white text-xs font-bold py-2.5 px-4 rounded-xl flex items-center justify-center gap-1.5 transition-colors"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">download</span>
                                                Cargar al Calendario
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Sub-view: VER HALLAZGOS (KANBAN BOARD POR CRITICIDAD) */}
                    {activeTab === 'hallazgos' && activeSubTab === 'ver' && (
                        <div className="space-y-6 animate-fade-in flex flex-col h-full overflow-hidden">
                            {/* Header description */}
                            <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-wrap gap-4 items-center justify-between">
                                <div>
                                    <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                        <span className="material-symbols-outlined text-primary">view_kanban</span>
                                        Tablero de Seguimiento de Hallazgos SSOMA
                                    </h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Ciclo de vida: Borrador ➜ Reportado ➜ En Revisión ➜ Cerrado. Las acciones y permisos se gestionan desde los detalles de cada tarjeta.
                                    </p>
                                </div>
                                <div className="text-xs font-bold px-3 py-1 bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-400 rounded-lg">
                                    {findingsList.filter(f => f.status === 'Reportado' && f.severity === 'alto').length} Hallazgos Reportados de Criticidad Alta
                                </div>
                            </div>

                            {/* Kanban Board Container */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-start overflow-visible min-h-[500px]">
                                
                                {/* COLUMN 1: BORRADORES */}
                                <div className="bg-slate-100 dark:bg-[#151f2b] p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4">
                                    <div className="flex items-center justify-between pb-2 border-b border-slate-200 dark:border-slate-850">
                                        <span className="text-xs font-extrabold uppercase text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                                            <span className="size-2 rounded-full bg-slate-500"></span>
                                            Borradores
                                        </span>
                                        <span className="text-xs font-black text-slate-400">{findingsList.filter(f => f.status === 'Borrador').length}</span>
                                    </div>
                                    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
                                        {findingsList.filter(f => f.status === 'Borrador').length === 0 ? (
                                            <p className="text-xs text-center text-slate-400 py-6">Sin borradores</p>
                                        ) : (
                                            findingsList.filter(f => f.status === 'Borrador').map(f => (
                                                <FindingCard key={f.id} finding={f} onOpenDetail={setShowFindingDetailModal} onEditBorrador={handleEditBorrador} />
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* COLUMN 2: REPORTADOS */}
                                <div className="bg-slate-100 dark:bg-[#151f2b] p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4">
                                    <div className="flex items-center justify-between pb-2 border-b border-slate-200 dark:border-slate-850">
                                        <span className="text-xs font-extrabold uppercase text-orange-600 dark:text-orange-400 flex items-center gap-1.5">
                                            <span className="size-2 rounded-full bg-orange-600 animate-ping"></span>
                                            Reportados
                                        </span>
                                        <span className="text-xs font-black text-slate-400">{findingsList.filter(f => f.status === 'Reportado').length}</span>
                                    </div>
                                    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
                                        {findingsList.filter(f => f.status === 'Reportado').length === 0 ? (
                                            <p className="text-xs text-center text-slate-400 py-6">Sin hallazgos reportados</p>
                                        ) : (
                                            findingsList.filter(f => f.status === 'Reportado').map(f => (
                                                <FindingCard key={f.id} finding={f} onOpenDetail={setShowFindingDetailModal} />
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* COLUMN 3: EN REVISIÓN */}
                                <div className="bg-slate-100 dark:bg-[#151f2b] p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4">
                                    <div className="flex items-center justify-between pb-2 border-b border-slate-200 dark:border-slate-850">
                                        <span className="text-xs font-extrabold uppercase text-blue-650 dark:text-blue-400 flex items-center gap-1.5">
                                            <span className="size-2 rounded-full bg-blue-500 animate-pulse"></span>
                                            En Revisión
                                        </span>
                                        <span className="text-xs font-black text-slate-400">{findingsList.filter(f => f.status === 'En Revision').length}</span>
                                    </div>
                                    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
                                        {findingsList.filter(f => f.status === 'En Revision').length === 0 ? (
                                            <p className="text-xs text-center text-slate-400 py-6">Sin hallazgos en revisión</p>
                                        ) : (
                                            findingsList.filter(f => f.status === 'En Revision').map(f => (
                                                <FindingCard key={f.id} finding={f} onOpenDetail={setShowFindingDetailModal} />
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* COLUMN 4: CERRADOS */}
                                <div className="bg-slate-100 dark:bg-[#151f2b] p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4">
                                    <div className="flex items-center justify-between pb-2 border-b border-slate-200 dark:border-slate-850">
                                        <span className="text-xs font-extrabold uppercase text-green-600 dark:text-green-400 flex items-center gap-1.5">
                                            <span className="size-2 rounded-full bg-green-500"></span>
                                            Cerrados
                                        </span>
                                        <span className="text-xs font-black text-slate-400">{findingsList.filter(f => f.status === 'Cerrado').length}</span>
                                    </div>
                                    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
                                        {findingsList.filter(f => f.status === 'Cerrado').length === 0 ? (
                                            <p className="text-xs text-center text-slate-400 py-6">Sin hallazgos cerrados</p>
                                        ) : (
                                            findingsList.filter(f => f.status === 'Cerrado').map(f => (
                                                <FindingCard key={f.id} finding={f} onOpenDetail={setShowFindingDetailModal} />
                                            ))
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}

                    {/* Sub-view: REPORTAR HALLAZGO */}
                    {activeTab === 'hallazgos' && activeSubTab === 'reportar' && (
                        <div className="animate-fade-in max-w-2xl mx-auto">
                            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
                                <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
                                    <span className="material-symbols-outlined text-[30px] text-rose-500">{editingBorradorId ? 'edit_square' : 'add_a_photo'}</span>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-800 dark:text-white">{editingBorradorId ? 'Completar Levantamiento de Borrador' : 'Reportar Desviación en Terreno'}</h3>
                                        <p className="text-xs text-slate-500">Optimizado para ingreso rápido en terreno (Celular/Tablet)</p>
                                    </div>
                                </div>

                                <form onSubmit={handleReportFinding} className="space-y-5">
                                    {/* 1. Titulo */}
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-slate-750 dark:text-slate-200">Título Breve del Hallazgo *</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="Ej: Filtración de agua en zona de tableros eléctricos"
                                            value={newFindTitle}
                                            onChange={e => setNewFindTitle(e.target.value)}
                                            className="text-xs p-2.5 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none focus:ring-1 focus:ring-primary focus:border-primary w-full"
                                        />
                                    </div>

                                    {/* 2. Tipo & Ubicación Estructurada */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-750 dark:text-slate-200">Tipo de Hallazgo *</label>
                                            <select
                                                required
                                                value={newFindType}
                                                onChange={e => setNewFindType(e.target.value as 'SSO' | 'MA')}
                                                className="text-xs p-2.5 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none focus:ring-1 focus:ring-primary focus:border-primary w-full font-medium"
                                            >
                                                <option value="SSO">SSO (Seguridad y Salud Ocupacional)</option>
                                                <option value="MA">MA (Medio Ambiente)</option>
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-750 dark:text-slate-200">Ubicación Estructurada *</label>
                                            <select
                                                required
                                                value={newFindLocation}
                                                onChange={e => setNewFindLocation(e.target.value)}
                                                className="text-xs p-2.5 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none focus:ring-1 focus:ring-primary focus:border-primary w-full font-medium"
                                            >
                                                <option value="">Seleccione Ubicación...</option>
                                                {levels.map(level => {
                                                    const levelUnits = (units || []).filter(u => u.levelId === level.id);
                                                    return (
                                                        <React.Fragment key={level.id}>
                                                            <option value={`level-${level.id}`} className="font-bold text-slate-900 dark:text-white">
                                                                {level.name}
                                                            </option>
                                                            {levelUnits.map(unit => (
                                                                <option key={unit.id} value={`unit-${unit.id}`}>
                                                                    &nbsp;&nbsp;&nbsp;&nbsp;↳ {unit.name}
                                                                </option>
                                                            ))}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    </div>

                                    {/* 3. Responsable & 4. Evento no Deseado */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-750 dark:text-slate-200">Asignar a Responsable *</label>
                                            <select
                                                required
                                                value={newFindResp}
                                                onChange={e => setNewFindResp(e.target.value)}
                                                className="text-xs p-2.5 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none focus:ring-1 focus:ring-primary focus:border-primary w-full font-medium"
                                            >
                                                <option value="">Seleccione Responsable...</option>
                                                {sortedResponsibles.map(r => (
                                                    <option key={r.id} value={r.id}>{r.name} ({r.role || 'Usuario'})</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-750 dark:text-slate-200">Evento no Deseado</label>
                                            <select
                                                value={newFindEvent}
                                                onChange={e => setNewFindEvent(e.target.value)}
                                                className="text-xs p-2.5 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none focus:ring-1 focus:ring-primary focus:border-primary w-full font-medium"
                                            >
                                                <option value="">Seleccione Evento No Deseado...</option>
                                                {globalSSOMAEvents.map(group => {
                                                    if (!group.causes || group.causes.length === 0) return null;
                                                    return (
                                                        <optgroup key={group.id} label={group.name} className="font-bold text-slate-500 bg-slate-50 dark:bg-slate-800">
                                                            {group.causes.map(cause => (
                                                                <option key={cause.id} value={cause.name} className="text-slate-800 dark:text-white font-medium">
                                                                    {cause.name}
                                                                </option>
                                                            ))}
                                                        </optgroup>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    </div>

                                    {/* 5. Nivel de Criticidad */}
                                    <div className="flex flex-col gap-2">
                                        <label className="text-xs font-bold text-slate-750 dark:text-slate-200">Nivel de Criticidad *</label>
                                        <div className="grid grid-cols-4 gap-2">
                                            {[
                                                { id: 'leve', label: 'Leve', color: 'border-green-500 text-green-700 bg-green-50/50 dark:bg-green-900/10 dark:text-green-400' },
                                                { id: 'medio', label: 'Medio', color: 'border-yellow-500 text-yellow-700 bg-yellow-50/50 dark:bg-yellow-900/10 dark:text-yellow-400' },
                                                { id: 'alto', label: 'Alto', color: 'border-orange-500 text-orange-700 bg-orange-50/50 dark:bg-orange-900/10 dark:text-orange-400' },
                                                { id: 'positivo', label: 'Positivo', color: 'border-blue-500 text-blue-700 bg-blue-50/50 dark:bg-blue-900/10 dark:text-blue-400' }
                                            ].map(opt => (
                                                <button
                                                    key={opt.id}
                                                    type="button"
                                                    onClick={() => {
                                                        setNewFindSeverity(opt.id as any);
                                                    }}
                                                    className={`py-2.5 px-3 border rounded-xl font-bold text-xs text-center transition-all ${
                                                        newFindSeverity === opt.id
                                                            ? `${opt.color} ring-2 ring-primary border-transparent scale-[1.02]`
                                                            : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800'
                                                    }`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* 6. Fecha de Creación & 8. Fecha Esperada de Cierre */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-750 dark:text-slate-200">Fecha de Creación *</label>
                                            <input
                                                type="date"
                                                required
                                                value={newFindDateRaised}
                                                onChange={e => setNewFindDateRaised(e.target.value)}
                                                className="text-xs p-2.5 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none focus:ring-1 focus:ring-primary focus:border-primary w-full font-medium"
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <label className="text-xs font-bold text-slate-750 dark:text-slate-200">Fecha Esperada de Cierre</label>
                                            <input
                                                type="date"
                                                disabled={newFindSeverity === 'positivo'}
                                                value={newFindSeverity === 'positivo' ? '' : newFindTargetDate}
                                                onChange={e => setNewFindTargetDate(e.target.value)}
                                                className={`text-xs p-2.5 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none focus:ring-1 focus:ring-primary focus:border-primary w-full font-medium ${
                                                    newFindSeverity === 'positivo' ? 'opacity-40 cursor-not-allowed bg-slate-100 dark:bg-slate-800' : ''
                                                }`}
                                            />
                                            {newFindSeverity === 'positivo' && (
                                                <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium italic mt-0.5">
                                                    No requiere fecha de cierre por criticidad Positivo (Reconocimiento)
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* 7. Descripción */}
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-xs font-bold text-slate-750 dark:text-slate-200">Descripción / Detalles del Hallazgo *</label>
                                        <textarea
                                            rows={3}
                                            placeholder="Detalla detalladamente el acto o la condición subestándar reportada en terreno..."
                                            value={newFindDesc}
                                            onChange={e => setNewFindDesc(e.target.value)}
                                            className="text-xs p-2.5 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none focus:ring-1 focus:ring-primary focus:border-primary w-full"
                                        />
                                    </div>

                                    {/* 9. Registro Fotográfico */}
                                    <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700 space-y-3">
                                        <label className="text-xs font-bold text-slate-750 dark:text-slate-200 flex items-center gap-1.5">
                                            <span className="material-symbols-outlined text-[18px]">photo_camera</span>
                                            Registro Fotográfico (Cámara/Archivo)
                                        </label>
                                        
                                        <div 
                                            onClick={() => {
                                                if (!newFindCustomPhotoUrl) {
                                                    const randomPreset = PRESET_PHOTOS[Math.floor(Math.random() * PRESET_PHOTOS.length)];
                                                    setNewFindPhotoPreset(randomPreset.id);
                                                    setNewFindCustomPhotoUrl(randomPreset.url);
                                                }
                                            }}
                                            className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-900/40 cursor-pointer hover:border-primary transition-colors relative overflow-hidden group"
                                        >
                                            {newFindCustomPhotoUrl ? (
                                                <div 
                                                    className="relative w-full h-40 rounded-lg overflow-hidden border border-slate-100 cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPreviewImageUrl(newFindCustomPhotoUrl);
                                                    }}
                                                    title="Hacer clic para ver en tamaño completo"
                                                >
                                                    <img src={newFindCustomPhotoUrl} alt="Preview" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                                                    <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
                                                        <span className="material-symbols-outlined text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow">zoom_in</span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setNewFindPhotoPreset('');
                                                            setNewFindCustomPhotoUrl('');
                                                        }}
                                                        className="absolute top-2 right-2 bg-red-650 text-white rounded-full p-1.5 shadow hover:bg-red-700 flex items-center justify-center z-10"
                                                        title="Eliminar foto"
                                                    >
                                                        <span className="material-symbols-outlined text-xs">delete</span>
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="text-center space-y-1">
                                                    <span className="material-symbols-outlined text-3xl text-slate-400 group-hover:text-primary transition-colors">cloud_upload</span>
                                                    <p className="text-xs font-semibold text-slate-655 dark:text-slate-350">Haz clic para subir o capturar foto</p>
                                                    <p className="text-[10px] text-slate-400">Formatos soportados: JPG, PNG • Max: 5MB</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* 10. Submit Button */}
                                    <div className="flex gap-3 pt-2">
                                        {editingBorradorId && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEditingBorradorId(null);
                                                    setNewFindTitle('');
                                                    setNewFindDesc('');
                                                    setNewFindResp('');
                                                    setNewFindPhotoPreset('');
                                                    setNewFindCustomPhotoUrl('');
                                                    setNewFindLocation('');
                                                    setNewFindEvent('');
                                                    setNewFindDateRaised(new Date().toISOString().split('T')[0]);
                                                    setNewFindTargetDate(() => {
                                                        const d = new Date();
                                                        d.setDate(d.getDate() + 3);
                                                        return d.toISOString().split('T')[0];
                                                    });
                                                    setActiveSubTab('ver');
                                                }}
                                                className="flex-1 bg-slate-200 hover:bg-slate-355 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-white font-extrabold py-3 px-4 rounded-xl text-xs transition-colors"
                                            >
                                                Cancelar Edición
                                            </button>
                                        )}
                                        <button
                                            type="submit"
                                            className={`flex-1 text-white font-extrabold py-3 px-4 rounded-xl flex items-center justify-center gap-2 shadow-sm transition-all text-xs ${
                                                isFormComplete ? 'bg-primary hover:bg-primary-dark' : 'bg-slate-500 hover:bg-slate-650'
                                            }`}
                                        >
                                            <span className="material-symbols-outlined">{isFormComplete ? 'send' : 'save'}</span>
                                            {isFormComplete ? 'Reportar Hallazgo' : 'Guardar Borrador'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* --- MODAL: PROGRAMAR ACTIVIDAD --- */}
            {showPlanModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-[#1a2634] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden p-6 space-y-5 animate-in fade-in zoom-in duration-150">
                        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-3">
                            <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-primary">calendar_month</span>
                                Programar Nueva Actividad Preventiva
                            </h3>
                            <button onClick={() => setShowPlanModal(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <form onSubmit={handleProgramActivity} className="space-y-4">
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-200">Nombre de la Actividad</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Ej: Charla 5 minutos de orden en carpintería"
                                    value={newActTitle}
                                    onChange={e => setNewActTitle(e.target.value)}
                                    className="text-xs p-2 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none focus:ring-1 focus:ring-primary"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-bold text-slate-700 dark:text-slate-200">Tipo</label>
                                    <select
                                        value={newActType}
                                        onChange={e => setNewActType(e.target.value as any)}
                                        className="text-xs p-2 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none"
                                    >
                                        <option value="charla">Charla de 5 Minutos</option>
                                        <option value="epp">Entrega de EPP</option>
                                        <option value="inspeccion">Inspección Programada</option>
                                        <option value="comite">Comité Paritario</option>
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-bold text-slate-700 dark:text-slate-200">Responsable</label>
                                    <select
                                        value={newActResponsible}
                                        onChange={e => setNewActResponsible(e.target.value)}
                                        className="text-xs p-2 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none"
                                    >
                                        {sortedResponsibles.map(r => (
                                            <option key={r.id} value={r.id}>{r.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-bold text-slate-700 dark:text-slate-200">Fecha de Ejecución</label>
                                    <input
                                        type="date"
                                        required
                                        value={newActDate}
                                        onChange={e => setNewActDate(e.target.value)}
                                        className="text-xs p-2 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-bold text-slate-700 dark:text-slate-200">Hora</label>
                                    <input
                                        type="time"
                                        required
                                        value={newActTime}
                                        onChange={e => setNewActTime(e.target.value)}
                                        className="text-xs p-2 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none"
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-200">Notas / Recomendaciones</label>
                                <textarea
                                    rows={2}
                                    placeholder="Instrucciones adicionales para la ejecución..."
                                    value={newActNotes}
                                    onChange={e => setNewActNotes(e.target.value)}
                                    className="text-xs p-2 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none"
                                />
                            </div>

                            <div className="flex items-center gap-3 justify-end pt-3 border-t border-slate-100 dark:border-slate-700">
                                <button
                                    type="button"
                                    onClick={() => setShowPlanModal(false)}
                                    className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-350"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-primary hover:bg-primary-dark text-white text-xs font-bold rounded-lg"
                                >
                                    Agendar Actividad
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- MODAL: REGISTRAR EVIDENCIA Y FIRMA (COMPLETAR ACTIVIDAD) --- */}
            {showCompleteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-[#1a2634] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden p-6 space-y-4 animate-in fade-in zoom-in duration-150">
                        <div className="flex items-center justify-between border-b border-[#e7ecf3] dark:border-slate-700 pb-3">
                            <div>
                                <span className="text-[9px] font-extrabold bg-primary/10 text-primary px-2 py-0.5 rounded-full uppercase">{showCompleteModal.type}</span>
                                <h3 className="text-sm font-extrabold text-slate-800 dark:text-white mt-1">Registrar Cierre: {showCompleteModal.title}</h3>
                            </div>
                            <button onClick={() => setShowCompleteModal(null)} className="text-slate-400 hover:text-slate-650 dark:hover:text-slate-255">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <form onSubmit={handleCompleteActivity} className="space-y-4">
                            {/* Render fields dynamically based on activity type */}
                            {showCompleteModal.type === 'charla' && (
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-extrabold text-slate-700 dark:text-slate-200">Operarios Asistentes (Uno por línea)</label>
                                    <textarea
                                        rows={4}
                                        required
                                        placeholder="Juan Pérez&#10;Manuel Soto&#10;Carlos Ortiz"
                                        value={attendedWorkers}
                                        onChange={e => setAttendedWorkers(e.target.value)}
                                        className="text-xs p-2.5 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none focus:ring-1 focus:ring-primary"
                                    />
                                    <p className="text-[10px] text-slate-400">Ingresa la lista de trabajadores que presenciaron la charla para el registro oficial.</p>
                                </div>
                            )}

                            {showCompleteModal.type === 'epp' && (
                                <div className="space-y-3">
                                    <label className="text-xs font-extrabold text-slate-700 dark:text-slate-200">Confirmar Entrega de EPP:</label>
                                    <div className="bg-slate-50 dark:bg-slate-900/30 border border-slate-100 dark:border-slate-800 p-3 rounded-xl space-y-2">
                                        {showCompleteModal.eppItems?.map((item, idx) => (
                                            <label key={idx} className="flex items-center justify-between text-xs cursor-pointer p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors">
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!eppChecked[idx]}
                                                        onChange={e => setEppChecked({ ...eppChecked, [idx]: e.target.checked })}
                                                        className="rounded border-slate-300 text-primary focus:ring-primary"
                                                    />
                                                    <span className="font-bold text-slate-750 dark:text-slate-200">{item.name}</span>
                                                </div>
                                                <span className="text-[11px] font-black text-slate-500 bg-slate-200/50 dark:bg-slate-800 px-2 py-0.5 rounded">Cant: {item.quantity}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {showCompleteModal.type === 'inspeccion' && (
                                <div className="space-y-3">
                                    <label className="text-xs font-extrabold text-slate-700 dark:text-slate-200">Evaluación de Puntos de Control:</label>
                                    <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1">
                                        {showCompleteModal.inspeccionItems?.map((item, idx) => (
                                            <div key={idx} className="bg-slate-50 dark:bg-slate-900/30 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 flex flex-col gap-2">
                                                <p className="text-xs font-bold text-slate-700 dark:text-slate-200 leading-snug">{item.description}</p>
                                                <div className="flex gap-2">
                                                    {['cumple', 'no_cumple', 'n_a'].map(st => (
                                                        <button
                                                            key={st}
                                                            type="button"
                                                            onClick={() => setInspeccionCheck({ ...inspeccionCheck, [idx]: st as any })}
                                                            className={`flex-1 py-1 text-[10px] font-extrabold uppercase rounded-lg border transition-all ${
                                                                inspeccionCheck[idx] === st
                                                                    ? st === 'cumple' ? 'bg-green-500 text-white border-green-500' :
                                                                      st === 'no_cumple' ? 'bg-red-500 text-white border-red-500' :
                                                                      'bg-slate-500 text-white border-slate-500'
                                                                    : 'bg-white dark:bg-slate-850 text-slate-550 border-slate-200 dark:border-slate-700'
                                                            }`}
                                                        >
                                                            {st === 'cumple' ? 'Cumple' : st === 'no_cumple' ? 'No Cumple' : 'N/A'}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Firma electronica digitalizada simple */}
                            <div className="bg-slate-50 dark:bg-slate-900/60 p-3 rounded-xl border border-slate-200 dark:border-slate-750 flex flex-col gap-2">
                                <label className="text-[11px] font-extrabold uppercase text-slate-500 dark:text-slate-305 flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[16px]">edit_square</span>
                                    Firma Electrónica / Aprobación del Supervisor
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        required
                                        placeholder="Ingresa tus iniciales o nombre como firma digital"
                                        value={signatureName}
                                        onChange={e => setSignatureName(e.target.value)}
                                        className="flex-1 text-xs p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg outline-none"
                                    />
                                </div>
                                <div className="h-14 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-955 flex items-center justify-center text-[10px] text-slate-400 italic">
                                    {signatureName ? `Firma encriptada: [${signatureName.toUpperCase()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}]` : 'Simular trazo o firma en pantalla'}
                                </div>
                            </div>

                            {/* Submit */}
                            <div className="flex items-center gap-3 justify-end pt-3 border-t border-slate-100 dark:border-slate-700">
                                <button
                                    type="button"
                                    onClick={() => setShowCompleteModal(null)}
                                    className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-750"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded-lg shadow-sm"
                                >
                                    Registrar Actividad Realizada
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- MODAL: DETALLE DE HALLAZGO --- */}
            {showFindingDetailModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-[#1a2634] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden p-6 space-y-4 animate-in fade-in zoom-in duration-150">
                        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-3">
                            <div>
                                <span className={`text-[9px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                                    showFindingDetailModal.type === 'SSO' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30' : 'bg-green-100 text-green-700 dark:bg-green-900/30'
                                }`}>
                                    {showFindingDetailModal.type === 'SSO' ? 'Seguridad y Salud Ocupacional (SSO)' : 'Medio Ambiente (MA)'}
                                </span>
                                <h3 className="text-base font-extrabold text-slate-800 dark:text-white mt-1">Detalle del Hallazgo</h3>
                            </div>
                            <button onClick={() => setShowFindingDetailModal(null)} className="text-slate-400 hover:text-slate-655">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="space-y-4 text-xs">
                            {/* Photos container (Raised vs Closed) */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Evidencia Inicial</p>
                                    <div className="h-32 bg-slate-150 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 flex items-center justify-center relative group">
                                        {showFindingDetailModal.photoUrl ? (
                                            <div 
                                                className="w-full h-full cursor-pointer relative"
                                                onClick={() => setPreviewImageUrl(showFindingDetailModal.photoUrl!)}
                                                title="Hacer clic para ver en tamaño completo"
                                            >
                                                <img src={showFindingDetailModal.photoUrl} alt="Evidencia inicial" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                                    <span className="material-symbols-outlined text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow">zoom_in</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-[10px] text-slate-450 italic">Sin fotografía</span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Evidencia de Cierre</p>
                                    <div className="h-32 bg-slate-150 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 flex items-center justify-center relative group">
                                        {showFindingDetailModal.status === 'Cerrado' ? (
                                            <div 
                                                className="w-full h-full cursor-pointer relative"
                                                onClick={() => setPreviewImageUrl(showFindingDetailModal.closurePhotoUrl || 'https://images.unsplash.com/photo-1590069261209-f8e9b8642343?auto=format&fit=crop&q=80&w=400')}
                                                title="Hacer clic para ver en tamaño completo"
                                            >
                                                <img src={showFindingDetailModal.closurePhotoUrl || 'https://images.unsplash.com/photo-1590069261209-f8e9b8642343?auto=format&fit=crop&q=80&w=400'} alt="Evidencia de cierre" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                                    <span className="material-symbols-outlined text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow">zoom_in</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-[10px] text-slate-450 italic">Pendiente de resolución</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Data fields */}
                            <div className="space-y-2.5">
                                <h4 className="text-sm font-extrabold text-slate-800 dark:text-white leading-snug">{showFindingDetailModal.title}</h4>
                                <p className="text-slate-650 dark:text-slate-400 leading-relaxed">{showFindingDetailModal.description}</p>
                            </div>

                            {/* Details meta grid */}
                            <div className="grid grid-cols-2 gap-4 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-200 dark:border-slate-750 text-slate-600 dark:text-slate-450 leading-loose">
                                <div>
                                    <span className="font-bold text-slate-400 block text-[9px] uppercase">Criticidad</span>
                                    <span className={`font-black uppercase px-2 py-0.5 rounded text-[9px] ${
                                        showFindingDetailModal.severity === 'positivo' ? 'bg-blue-100 text-blue-700' :
                                        showFindingDetailModal.severity === 'alto' ? 'bg-red-100 text-red-700' :
                                        showFindingDetailModal.severity === 'medio' ? 'bg-orange-100 text-orange-700' :
                                        'bg-green-100 text-green-700'
                                    }`}>
                                        {showFindingDetailModal.severity}
                                    </span>
                                </div>
                                <div>
                                    <span className="font-bold text-slate-400 block text-[9px] uppercase">Reportado por</span>
                                    <span className="font-bold text-slate-800 dark:text-white">{showFindingDetailModal.reporterName}</span>
                                </div>
                                <div>
                                    <span className="font-bold text-slate-400 block text-[9px] uppercase">Fecha de Detección</span>
                                    <span className="font-bold text-slate-800 dark:text-white">{showFindingDetailModal.dateRaised}</span>
                                </div>
                                <div>
                                    <span className="font-bold text-slate-400 block text-[9px] uppercase">Fecha Límite Cierre</span>
                                    <span className={`font-bold ${showFindingDetailModal.status !== 'Cerrado' && showFindingDetailModal.targetDate && new Date(showFindingDetailModal.targetDate) < new Date() ? 'text-red-500 font-extrabold' : 'text-slate-800 dark:text-white'}`}>
                                        {showFindingDetailModal.targetDate || 'No requiere'}
                                    </span>
                                </div>
                                <div>
                                    <span className="font-bold text-slate-400 block text-[9px] uppercase">Responsable Cierre</span>
                                    <span className="font-bold text-slate-800 dark:text-white">{getResponsibleName(showFindingDetailModal.responsibleId)}</span>
                                </div>
                                <div>
                                    <span className="font-bold text-slate-400 block text-[9px] uppercase">Estado Actual</span>
                                    <span className="font-bold text-slate-800 dark:text-white">{showFindingDetailModal.status}</span>
                                </div>
                                <div>
                                    <span className="font-bold text-slate-400 block text-[9px] uppercase">Ubicación</span>
                                    <span className="font-bold text-slate-800 dark:text-white">{showFindingDetailModal.locationName || 'No especificada'}</span>
                                </div>
                                <div>
                                    <span className="font-bold text-slate-400 block text-[9px] uppercase">Evento No Deseado</span>
                                    <span className="font-bold text-slate-800 dark:text-white">{showFindingDetailModal.eventoNoDeseado || 'No aplica/Ninguno'}</span>
                                </div>
                            </div>

                            {/* Rejected status indicator */}
                            {showFindingDetailModal.rejectedByExpert && (
                                <div className="bg-red-50 dark:bg-red-955/10 p-4 rounded-xl border border-red-200 dark:border-red-900/30 space-y-1">
                                    <span className="font-bold text-[9px] text-red-650 dark:text-red-400 uppercase flex items-center gap-1">
                                        <span className="material-symbols-outlined text-[14px]">warning</span>
                                        Rechazado por Experto
                                    </span>
                                    <p className="font-semibold text-red-800 dark:text-red-300">"{showFindingDetailModal.rejectedComment}"</p>
                                </div>
                            )}

                            {/* Cierre Técnico details if En Revision or Cerrado */}
                            {(showFindingDetailModal.status === 'En Revision' || showFindingDetailModal.status === 'Cerrado') && showFindingDetailModal.closureNotes && (
                                <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-200 dark:border-slate-750 space-y-2">
                                    <span className="font-bold text-[9px] text-slate-500 dark:text-slate-400 uppercase">Detalles del Cierre Técnico (Reparación)</span>
                                    <p className="font-semibold text-slate-700 dark:text-slate-250">"{showFindingDetailModal.closureNotes}"</p>
                                    {showFindingDetailModal.closurePhotoUrl && (
                                        <div 
                                            className="h-32 bg-slate-150 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 max-w-xs cursor-pointer relative group"
                                            onClick={() => setPreviewImageUrl(showFindingDetailModal.closurePhotoUrl!)}
                                            title="Hacer clic para ver en tamaño completo"
                                        >
                                            <img src={showFindingDetailModal.closurePhotoUrl} alt="Foto reparación" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                                <span className="material-symbols-outlined text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow">zoom_in</span>
                                            </div>
                                        </div>
                                    )}
                                    {showFindingDetailModal.closureDate && (
                                        <p className="text-[10px] text-slate-500 mt-1">Fecha: {showFindingDetailModal.closureDate}</p>
                                    )}
                                </div>
                            )}

                            {/* ACTION: BORRADOR */}
                            {showFindingDetailModal.status === 'Borrador' && (
                                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handleEditBorrador(showFindingDetailModal);
                                            setShowFindingDetailModal(null);
                                        }}
                                        className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-extrabold rounded-lg text-xs"
                                    >
                                        Editar y Completar Borrador
                                    </button>
                                </div>
                            )}

                            {/* ACTION: REPORTADO */}
                            {showFindingDetailModal.status === 'Reportado' && (() => {
                                const hasPermission = currentUser.id === showFindingDetailModal.responsibleId || isUserAdmin;
                                return (
                                    <div className="space-y-4">
                                        {hasPermission ? (
                                            <>
                                                {/* Reasignar Responsable */}
                                                <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
                                                    <label className="text-xs font-bold text-slate-700 dark:text-slate-200">Reasignar Responsable</label>
                                                    <div className="flex gap-2">
                                                        <select
                                                            value={selectedReassignId}
                                                            onChange={e => setSelectedReassignId(e.target.value)}
                                                            className="text-xs p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg outline-none flex-1 font-medium text-slate-800 dark:text-white"
                                                        >
                                                            <option value="">Seleccione responsable...</option>
                                                            {sortedResponsibles.map(r => (
                                                                <option key={r.id} value={r.id}>{r.name} ({r.role})</option>
                                                            ))}
                                                        </select>
                                                        <button
                                                            type="button"
                                                            onClick={handleReassign}
                                                            className="px-4 py-2 bg-primary hover:bg-primary-dark text-white text-xs font-extrabold rounded-lg transition-colors"
                                                        >
                                                            Reasignar
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Formulario Cierre Técnico */}
                                                <form onSubmit={handleCierreTecnico} className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-3">
                                                    <h5 className="text-xs font-extrabold text-slate-800 dark:text-white uppercase tracking-wider">Formulario de Cierre Técnico</h5>
                                                    
                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400">Acción Correctiva Realizada *</label>
                                                        <textarea
                                                            required
                                                            rows={2}
                                                            placeholder="Describe detalladamente qué acción se tomó..."
                                                            value={cierreComment}
                                                            onChange={e => setCierreComment(e.target.value)}
                                                            className="text-xs p-2.5 border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg outline-none focus:ring-1 focus:ring-primary w-full text-slate-800 dark:text-white"
                                                        />
                                                    </div>

                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400">Foto de Evidencia de Reparación *</label>
                                                        <div 
                                                            onClick={() => {
                                                                const randomPreset = PRESET_PHOTOS[Math.floor(Math.random() * PRESET_PHOTOS.length)];
                                                                setCierrePhoto(randomPreset.url);
                                                            }}
                                                            className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-3 bg-white dark:bg-slate-900/40 cursor-pointer hover:border-primary transition-colors relative overflow-hidden h-24 text-center"
                                                        >
                                                            {cierrePhoto ? (
                                                                <img src={cierrePhoto} alt="Reparación Preview" className="w-full h-full object-cover rounded-lg" />
                                                            ) : (
                                                                <div className="space-y-1">
                                                                    <span className="material-symbols-outlined text-xl text-slate-400">add_a_photo</span>
                                                                    <p className="text-[10px] font-semibold text-slate-655 dark:text-slate-350">Haz clic para capturar foto de reparación</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="submit"
                                                        className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white font-extrabold rounded-lg text-xs transition-colors"
                                                    >
                                                        Resolución de Hallazgo (Enviar a Revisión)
                                                    </button>
                                                </form>
                                            </>
                                        ) : (
                                            <div className="bg-amber-50 dark:bg-amber-955/20 p-3 rounded-xl border border-amber-200 dark:border-amber-900/50 text-[11px] text-amber-850 dark:text-amber-300 font-semibold flex items-center gap-1.5 leading-relaxed">
                                                <span className="material-symbols-outlined text-[16px] text-amber-600">lock</span>
                                                <span>Solo el responsable asignado ({getResponsibleName(showFindingDetailModal.responsibleId)}) o un Administrador de Obra pueden interactuar y resolver este hallazgo.</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* ACTION: EN REVISIÓN */}
                            {showFindingDetailModal.status === 'En Revision' && (
                                <div className="space-y-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                                    {isUserExperto ? (
                                        <div className="space-y-3">
                                            <h5 className="text-xs font-extrabold text-slate-800 dark:text-white uppercase tracking-wider">Decisiones del Experto SSOMA</h5>
                                            
                                            <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700 space-y-3">
                                                <div className="flex flex-col gap-1">
                                                    <label className="text-[10px] font-bold text-slate-500 dark:text-slate-450 block">Si decide rechazar, ingrese el motivo:</label>
                                                    <textarea
                                                        rows={2}
                                                        placeholder="Motivo obligatorio en caso de rechazo..."
                                                        value={rejectionComment}
                                                        onChange={e => setRejectionComment(e.target.value)}
                                                        className="text-xs p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg outline-none w-full text-slate-800 dark:text-white"
                                                    />
                                                </div>

                                                <div className="flex gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={handleRechazar}
                                                        className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white font-extrabold rounded-lg text-xs transition-colors"
                                                    >
                                                        Rechazar Resolución
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleAprobar}
                                                        className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white font-extrabold rounded-lg text-xs transition-colors"
                                                    >
                                                        Aprobar (Levantamiento)
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-amber-50 dark:bg-amber-955/20 p-3 rounded-xl border border-amber-200 dark:border-amber-900/50 text-[11px] text-amber-850 dark:text-amber-300 font-semibold flex items-center gap-1.5 leading-relaxed">
                                            <span className="material-symbols-outlined text-[16px] text-amber-600">lock_person</span>
                                            <span>Solo un Experto SSOMA (Prevencionista) o un Administrador pueden evaluar y dar el cierre definitivo a este hallazgo.</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ACTION: CERRADO */}
                            {showFindingDetailModal.status === 'Cerrado' && (
                                <div className="bg-green-50 dark:bg-green-950/20 p-3 rounded-xl border border-green-200 dark:border-green-900/50 text-[11px] text-green-800 dark:text-green-300 font-semibold flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                    <span>Este hallazgo está cerrado y archivado en modo Solo Lectura.</span>
                                </div>
                            )}

                        </div>

                        <div className="flex justify-end pt-3 border-t border-slate-100 dark:border-slate-700">
                            <button
                                onClick={() => {
                                    setShowFindingDetailModal(null);
                                    setRejectionComment('');
                                }}
                                className="px-5 py-2 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white text-xs font-bold rounded-lg shadow-sm"
                            >
                                Cerrar Ventana
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Lightbox / Visor de Imagen en Tamaño Completo */}
            {previewImageUrl && (
                <div 
                    className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200"
                    onClick={() => setPreviewImageUrl(null)}
                >
                    <button 
                        type="button"
                        onClick={() => setPreviewImageUrl(null)} 
                        className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/50 hover:bg-black/75 p-2.5 rounded-full transition-colors z-10 flex items-center justify-center shadow-lg border border-white/10"
                        title="Cerrar (Esc)"
                    >
                        <span className="material-symbols-outlined text-2xl">close</span>
                    </button>
                    <div 
                        className="relative max-w-[92vw] max-h-[88vh] flex items-center justify-center select-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img 
                            src={previewImageUrl} 
                            alt="Vista previa ampliada" 
                            className="max-w-[92vw] max-h-[88vh] object-contain rounded-xl shadow-2xl ring-1 ring-white/10"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

// Subcomponent: Kanban Card for findings to keep main component cleaner
function FindingCard({
    finding,
    onOpenDetail,
    onEditBorrador
}: {
    finding: PreventionFinding;
    onOpenDetail: (f: PreventionFinding) => void;
    onEditBorrador?: (finding: PreventionFinding) => void;
}) {
    return (
        <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-750 shadow-sm flex flex-col justify-between gap-3 hover:shadow-md transition-shadow group relative">
            <div className="space-y-2">
                {/* Severity Badge */}
                <div className="flex items-center justify-between">
                    <span className={`text-[8px] font-extrabold uppercase px-1.5 py-0.5 rounded ${
                        finding.severity === 'positivo' ? 'bg-blue-50 text-blue-600 dark:bg-blue-955/40 dark:text-blue-400' :
                        finding.severity === 'alto' ? 'bg-red-50 text-red-600 dark:bg-red-955/40 dark:text-red-400' :
                        finding.severity === 'medio' ? 'bg-orange-50 text-orange-600 dark:bg-orange-955/40 dark:text-orange-400' :
                        'bg-green-50 text-green-600 dark:bg-green-955/40 dark:text-green-400'
                    }`}>
                        {finding.severity === 'positivo' ? '✨' : '🚨'} {finding.severity}
                    </span>
                    <span className="text-[10px] text-slate-450 font-medium">{finding.dateRaised}</span>
                </div>

                {/* Title */}
                <h4 className="text-xs font-extrabold text-slate-800 dark:text-white line-clamp-2 leading-snug cursor-pointer hover:text-primary transition-colors" onClick={() => onOpenDetail(finding)}>
                    {finding.title}
                </h4>

                {/* Rejected by Expert tag */}
                {finding.rejectedByExpert && (
                    <div className="bg-red-50 text-red-650 dark:bg-red-950/10 dark:text-red-400 text-[9px] font-extrabold px-1.5 py-0.5 rounded border border-red-150 dark:border-red-900/30 flex items-center gap-1">
                        <span className="material-symbols-outlined text-[12px]">warning</span>
                        <span>Rechazado por Experto</span>
                    </div>
                )}

                {/* Thumbnail image if exists */}
                {finding.photoUrl && (
                    <div className="h-16 w-full rounded-lg overflow-hidden bg-slate-100 border border-slate-150 dark:border-slate-700 cursor-pointer" onClick={() => onOpenDetail(finding)}>
                        <img src={finding.photoUrl} alt="Thumbnail" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                )}
            </div>

            {/* Bottom tools */}
            <div className="pt-2 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <button
                    onClick={() => onOpenDetail(finding)}
                    className="text-[10px] font-bold text-primary hover:underline flex items-center gap-0.5"
                >
                    <span className="material-symbols-outlined text-[12px]">visibility</span>
                    Ver Detalles
                </button>

                {finding.status === 'Borrador' && onEditBorrador && (
                    <button
                        onClick={() => onEditBorrador(finding)}
                        className="text-[10px] font-bold text-amber-600 hover:underline flex items-center gap-0.5"
                    >
                        <span className="material-symbols-outlined text-[12px]">edit</span>
                        Completar
                    </button>
                )}
            </div>
        </div>
    );
}
